import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { colors, fonts } from "../theme";
import { View, Text, Card, ScreenHeader, Loading, EmptyState, SectionTitle, ProgressBar } from "../ui";
import { ProgressRing } from "../ProgressRing";
import { useI18n } from "../i18n";
import { apiAdminResults } from "../api";
import { AREAS, LESSONS, t } from "../content";
import { readinessOf } from "../adminUtil";

function Row({ l, v }) {
  return (
    <View row style={{ justifyContent: "space-between" }}>
      <Text style={{ fontWeight: fonts.regular, fontSize: 13, color: colors.muted }}>{l}</Text>
      <Text style={{ fontWeight: fonts.bold, fontSize: 13, color: colors.onSurface }}>{v}</Text>
    </View>
  );
}

export default function AdminLearner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tr, lang } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { apiAdminResults().then(setData).catch(() => setData([])).finally(() => setLoading(false)); }, []);

  const row = data?.find((r) => r.user.id === id);
  if (loading) return (<View style={{ flex: 1, minHeight: "100vh", backgroundColor: colors.surface }}><ScreenHeader title={tr("learnerDetail")} onBack={() => navigate(-1)} /><Loading /></View>);
  if (!row) return (<View style={{ flex: 1, minHeight: "100vh", backgroundColor: colors.surface }}><ScreenHeader title={tr("learnerDetail")} onBack={() => navigate(-1)} /><EmptyState icon="user" title={tr("noUsers")} /></View>);

  const d = row.data || {};
  const readiness = readinessOf(d);
  const answered = d.answered || 0;
  const acc = answered ? Math.round(((d.correct || 0) / answered) * 100) : 0;
  const lessonsDone = Object.values(d.lessons || {}).filter((l) => l.completed).length;
  const exams = (d.exams || []).slice(0, 12).reverse();

  return (
    <View style={{ flex: 1, minHeight: "100vh", backgroundColor: colors.surfaceSecondary }}>
      <ScreenHeader title={row.user.name} subtitle={row.user.email} onBack={() => navigate(-1)} />
      <View style={{ padding: 20 }}>
        <Card style={{ flexDirection: "row", alignItems: "center", gap: 18, marginBottom: 16 }}>
          <ProgressRing progress={readiness} size={92} stroke={10} label={`${readiness}%`} />
          <View style={{ flex: 1, gap: 6 }}>
            <Row l={tr("questionsAnswered")} v={String(answered)} />
            <Row l={tr("accuracy")} v={`${acc}%`} />
            <Row l={tr("lessonsDone")} v={`${lessonsDone}/${LESSONS.length}`} />
            <Row l={tr("streak")} v={String(d.streak?.count || 0)} />
          </View>
        </Card>

        <SectionTitle>{tr("topicMastery")}</SectionTitle>
        {AREAS.map((area) => {
          const a = d.areaStats?.[area.id];
          const pct = a && a.answered ? Math.round((a.correct / a.answered) * 100) : null;
          const color = pct === null ? colors.border : pct >= 75 ? colors.success : pct >= 50 ? colors.warning : colors.error;
          return (
            <Card key={area.id} style={{ marginBottom: 10 }}>
              <View row style={{ justifyContent: "space-between", marginBottom: 8 }}>
                <Text numberOfLines={1} style={{ flex: 1, fontWeight: fonts.semibold, fontSize: 13, color: colors.onSurface }}>{area.num} · {t(area.title, lang)}</Text>
                <Text style={{ fontWeight: fonts.bold, fontSize: 13, color: pct === null ? colors.muted : color }}>{pct === null ? "—" : `${pct}%`}</Text>
              </View>
              <ProgressBar progress={pct ?? 0} color={pct === null ? colors.border : color} />
            </Card>
          );
        })}

        <SectionTitle style={{ marginTop: 8 }}>{tr("examHistory")}</SectionTitle>
        {exams.length === 0 ? (
          <Card><Text style={{ fontWeight: fonts.regular, fontSize: 13, color: colors.muted, textAlign: "center", padding: "6px 0", width: "100%" }}>{tr("noExamsYet")}</Text></Card>
        ) : (
          <Card>
            <View row style={{ alignItems: "flex-end", gap: 8, height: 130, marginBottom: 8 }}>
              {exams.map((e, i) => {
                const good = e.score >= 60;
                return (
                  <View key={i} style={{ flex: 1, alignItems: "center", gap: 4, justifyContent: "flex-end" }}>
                    <Text style={{ fontWeight: fonts.bold, fontSize: 10, color: good ? colors.success : colors.error }}>{e.score}</Text>
                    <div style={{ width: "70%", height: Math.max(6, (e.score / 100) * 96), backgroundColor: good ? colors.success : colors.error, borderRadius: 6 }} />
                  </View>
                );
              })}
            </View>
            <Text style={{ fontWeight: fonts.regular, fontSize: 11, color: colors.muted, textAlign: "center", width: "100%" }}>{lang === "de" ? "Ergebnisse (älteste → neueste)" : "Scores (oldest → newest)"}</Text>
          </Card>
        )}
      </View>
    </View>
  );
}
